/**
 * ActivityReportsPage
 *
 * Coordinator/Manager-facing landing for the Somos Activity Tracking
 * reports. Two stacked panels:
 *
 *   1. AttendanceReportPanel — per-promotora attendance at Hub Meetings
 *      and Training/Workshops (supervisor+ only; self-gates with 403 →
 *      empty-state when a Promotora lands here directly).
 *   2. OutreachReportPanel  — aggregate outreach activity counts with
 *      filter + CSV/XLSX export. Promotora-scoped server-side.
 *
 * The page itself doesn't gate by role — the panels handle their own
 * authorization. That keeps the routing dumb and lets each panel show
 * a sensible explanation if a frontline-staff user navigates in.
 */

import React from 'react';
import { BarChart3, FileDown } from 'lucide-react';
import { AttendanceReportPanel } from './AttendanceReportPanel';
import { OutreachReportPanel } from './OutreachReportPanel';
import { OutreachReviewQueue } from './OutreachReviewQueue';
import { useTabbedChrome } from '@/components/weavein';

export const ActivityReportsPage: React.FC = () => {
  const tabbed = useTabbedChrome();
  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <header>
        {!tabbed && (
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-blue-600" />
          Activity Reports
        </h1>
        )}
        <p className="text-sm text-gray-500 mt-1">
          Per-staff attendance + outreach activity totals for contract reporting.
          <span className="inline-flex items-center gap-1 ml-2 text-xs text-gray-400">
            <FileDown className="w-3 h-3" />
            Export to CSV (server) or XLSX (client)
          </span>
        </p>
      </header>

      <OutreachReviewQueue />
      <AttendanceReportPanel />
      <OutreachReportPanel />

      <p className="text-xs text-gray-400 pt-2">
        Additional tailored quarterly reports can be added on request.
      </p>
    </div>
  );
};

export default ActivityReportsPage;
